/**
 * Role and Permissions Service
 * Handles API calls for role and permission management
 */

import { ApiService } from './api';

export interface Permission {
  id?: number;
  name: string;
  display_name?: string;
  description?: string;
  category?: string;
}

export interface Role {
  id: number;
  name: string;
  display_name: string;
  description: string;
  permissions: string[];
  is_system: boolean;
  is_active: boolean;
  users_count?: number;
  users?: Array<{
    id: number;
    name: string;
    email: string;
    role: string;
    is_active: boolean;
  }>;
  created_at: string;
  updated_at: string;
}

export interface RoleStats {
  total_roles: number;
  active_roles: number;
  system_roles: number;
  custom_roles: number;
  role_distribution: Array<{
    name: string;
    display_name: string;
    user_count: number;
  }>;
}

export interface CreateRoleData {
  name: string;
  display_name: string;
  description: string;
  permissions: string[];
  is_active?: boolean;
}

export interface UpdateRoleData {
  name?: string;
  display_name?: string;
  description?: string;
  permissions?: string[];
  is_active?: boolean;
}

class RoleService {
  /**
   * Get all roles with pagination
   */
  static async getRoles(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    active?: boolean;
  }): Promise<{
    roles: Role[];
    total: number;
    current_page: number;
    last_page: number;
    meta?: any;
  }> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: {
          data: Role[];
          total: number;
          current_page: number;
          last_page: number;
        };
        meta?: any;
      }>('/admin/roles', { params });
      
      return {
        roles: response.data.data,
        total: response.data.total,
        current_page: response.data.current_page,
        last_page: response.data.last_page,
        meta: response.meta
      };
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw new Error('Failed to load roles');
    }
  }

  /**
   * Get a specific role by ID
   */
  static async getRole(id: number): Promise<Role> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: Role;
      }>(`/admin/roles/${id}`);
      
      return response.data;
    } catch (error) {
      console.error('Error fetching role:', error);
      throw new Error('Failed to load role');
    }
  }

  /**
   * Create a new role
   */
  static async createRole(data: CreateRoleData): Promise<Role> {
    try {
      const response = await ApiService.post<{
        success: boolean;
        data: Role;
        message: string;
      }>('/admin/roles', data);
      
      return response.data;
    } catch (error) {
      console.error('Error creating role:', error);
      throw new Error('Failed to create role');
    }
  }

  /**
   * Update an existing role
   */
  static async updateRole(id: number, data: UpdateRoleData): Promise<Role> {
    try {
      const response = await ApiService.put<{
        success: boolean;
        data: Role;
        message: string;
      }>(`/admin/roles/${id}`, data);
      
      return response.data;
    } catch (error) {
      console.error('Error updating role:', error);
      throw new Error('Failed to update role');
    }
  }

  /**
   * Delete a role
   */
  static async deleteRole(id: number): Promise<void> {
    try {
      await ApiService.delete<{
        success: boolean;
        message: string;
      }>(`/admin/roles/${id}`);
    } catch (error) {
      console.error('Error deleting role:', error);
      throw new Error('Failed to delete role');
    }
  }

  /**
   * Get all permissions
   */
  static async getPermissions(): Promise<{
    permissions: string[];
    categories: Record<string, Record<string, string>>;
    grouped_permissions: Record<string, Record<string, string>>;
  }> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: {
          permissions: string[];
          categories: Record<string, Record<string, string>>;
          grouped_permissions: Record<string, Record<string, string>>;
        };
      }>('/admin/roles/permissions');
      
      return response.data;
    } catch (error) {
      console.error('Error fetching permissions:', error);
      throw new Error('Failed to load permissions');
    }
  }

  /**
   * Get permission categories (derived from permissions)
   */
  static async getPermissionCategories(): Promise<string[]> {
    try {
      const { grouped_permissions } = await this.getPermissions();
      return Object.keys(grouped_permissions);
    } catch (error) {
      console.error('Error fetching permission categories:', error);
      throw new Error('Failed to load permission categories');
    }
  }

  /**
   * Assign role to user
   */
  static async assignRoleToUser(userId: number, roleId: number): Promise<void> {
    try {
      await ApiService.post<{
        success: boolean;
        message: string;
      }>(`/admin/users/${userId}/roles`, { role_id: roleId });
    } catch (error) {
      console.error('Error assigning role to user:', error);
      throw new Error('Failed to assign role to user');
    }
  }

  /**
   * Remove role from user
   */
  static async removeRoleFromUser(userId: number, roleId: number): Promise<void> {
    try {
      await ApiService.delete<{
        success: boolean;
        message: string;
      }>(`/admin/users/${userId}/roles/${roleId}`);
    } catch (error) {
      console.error('Error removing role from user:', error);
      throw new Error('Failed to remove role from user');
    }
  }

  /**
   * Get role statistics
   */
  static async getRoleStats(): Promise<RoleStats> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        data: RoleStats;
      }>('/admin/roles/stats');
      
      return response.data;
    } catch (error) {
      console.error('Error fetching role stats:', error);
      throw new Error('Failed to load role statistics');
    }
  }

  /**
   * Sync permissions for a role
   */
  static async syncRolePermissions(roleId: number, permissionIds: number[]): Promise<void> {
    try {
      await ApiService.put<{
        success: boolean;
        message: string;
      }>(`/admin/roles/${roleId}/permissions`, { permissions: permissionIds });
    } catch (error) {
      console.error('Error syncing role permissions:', error);
      throw new Error('Failed to sync role permissions');
    }
  }

  /**
   * Get users with a specific role
   */
  static async getUsersWithRole(roleId: number, params?: {
    page?: number;
    limit?: number;
  }): Promise<{
    users: any[];
    total: number;
    current_page: number;
    last_page: number;
  }> {
    try {
      const response = await ApiService.get<{
        success: boolean;
        users: {
          data: any[];
          total: number;
          current_page: number;
          last_page: number;
        };
      }>(`/admin/roles/${roleId}/users`, { params });
      
      return {
        users: response.users.data,
        total: response.users.total,
        current_page: response.users.current_page,
        last_page: response.users.last_page
      };
    } catch (error) {
      console.error('Error fetching users with role:', error);
      throw new Error('Failed to load users with role');
    }
  }
}

export default RoleService;